import React, { useState } from 'react';
import { webFormatCurrency } from '@rb-serve-app/core';

interface AuthorizationItem {
  id: string;
  externalId: string;
  de043CardAcceptorNameLocation: string;
  responseReasonId: string;
  de006CardholderBillingAmount: string;
  eligibleRogersRedeemBonus: boolean;
  transactionDateTime: any;
  cashBackAmount: string;
}

interface RewardTransactionItemProps {
  authorizationItem: AuthorizationItem;
  rb_rewards_cashback: {
    rewardsCashback: {
      responseReasonId: string;
      eligibleforRogersRedemption: string;
      redeem: string;
      cashBackRedeemed: string;
      redemptionErrorMessage: string;
      redemptionErrorTitle: string;
    };
  };
  redeemRewardSearchData: {
    redeemerProfile: {
      preferences: {
        minAccountPurchaseThreshold: string;
      };
    };
  };
  rewardBalance: string;
  setRedeemErrorVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectionAuthorizationIndexData: React.Dispatch<React.SetStateAction<any>>;
  setConfirmRedeemVisible: React.Dispatch<React.SetStateAction<boolean>>;
  lang: any;
  displayMode: 'list' | 'table';
  formattedDate?: string;
}

const RewardTransactionItem: React.FC<RewardTransactionItemProps> = ({
  authorizationItem: transaction,
  rb_rewards_cashback,
  redeemRewardSearchData,
  rewardBalance,
  setRedeemErrorVisible,
  setSelectionAuthorizationIndexData,
  setConfirmRedeemVisible,
  lang,
  displayMode,
  formattedDate,
}) => {
  const formattedAmount = (amount: string | number) => {
    const parsedAmount = Number(amount);
    if (isNaN(parsedAmount) || parsedAmount === 0) {
      return webFormatCurrency(0, lang);
    }
    return webFormatCurrency(parsedAmount, lang);
  };

  const disableRedeemButton = parseFloat(rewardBalance) < 10;

  const handleRedeemClick = (item: AuthorizationItem) => {
    const thresholdAmount =
      redeemRewardSearchData?.redeemerProfile?.preferences
        ?.minAccountPurchaseThreshold ?? 10;
    const itemAmount = Number(item?.de006CardholderBillingAmount).toFixed(2);

    if (thresholdAmount > itemAmount) {
      setRedeemErrorVisible(true);
      return;
    }
    setSelectionAuthorizationIndexData(item);
    setConfirmRedeemVisible(true);
  };

  const isEligibleForRedemption =
    transaction?.responseReasonId ===
    rb_rewards_cashback?.rewardsCashback.responseReasonId;

  if (displayMode === 'table') {
    return (
      <tr className="bg-white">
        <td
          aria-label={formattedDate}
          className="px-4 py-4 whitespace-nowrap text-[15px] font-normal text-[#1f1f1f]"
        >
          {formattedDate}
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <p
            aria-label={transaction?.de043CardAcceptorNameLocation}
            className="text-[15px] text-[#1f1f1f] font-normal w-auto"
          >
            {transaction?.de043CardAcceptorNameLocation}
          </p>
          {isEligibleForRedemption &&
            transaction?.eligibleRogersRedeemBonus && (
              <span
                aria-label={
                  rb_rewards_cashback.rewardsCashback
                    .eligibleforRogersRedemption
                }
                className="text-[10.5px] text-[#1f1f1f] block lg:max-w-fit md:max-w-[200px] break-words whitespace-normal border border-[#003D46] bg-[#F1FDFF] px-3 mt-2"
              >
                {
                  rb_rewards_cashback.rewardsCashback
                    .eligibleforRogersRedemption
                }
              </span>
            )}
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <p
            aria-label={transaction?.de006CardholderBillingAmount}
            className="text-[15px] text-[#1f1f1f] font-normal"
          >
            {formattedAmount(transaction?.de006CardholderBillingAmount)}
          </p>
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
          {isEligibleForRedemption && (
            <button
              aria-label={rb_rewards_cashback.rewardsCashback.redeem}
              className={`${disableRedeemButton ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border' : 'bg-white text-[#DA291C] hover:bg-red-100 border border-[#DA291C]'} h-[48px] w-[100px] font-medium py-2 px-4 focus:outline-none focus:ring-2 focus:ring-opacity-50"`}
              onClick={() => handleRedeemClick(transaction)}
              disabled={disableRedeemButton}
            >
              {rb_rewards_cashback.rewardsCashback.redeem}
            </button>
          )}
          {!isEligibleForRedemption && (
            <>
              <p
                aria-label={transaction?.cashBackAmount}
                className="text-[15px] font-normal text-[#1E8240]"
              >
                {formattedAmount(transaction?.cashBackAmount)}
              </p>
              <p
                aria-label={
                  rb_rewards_cashback?.rewardsCashback.cashBackRedeemed
                }
                className="text-[15px] font-normal text-[#1E8240]"
              >
                {rb_rewards_cashback?.rewardsCashback.cashBackRedeemed}
              </p>
            </>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div className="bg-white">
      <div
        key={transaction?.id || transaction?.externalId}
        className="flex flex-row px-5 py-4"
      >
        <div className="flex flex-col flex-1 mr-5">
          <p
            aria-label={transaction?.de043CardAcceptorNameLocation}
            className="text-[15px] text-black font-semibold w-auto"
          >
            {transaction?.de043CardAcceptorNameLocation}
          </p>

          {isEligibleForRedemption && (
            <>
              <p
                aria-label={transaction?.de006CardholderBillingAmount}
                className="text-[15px] text-black w-auto"
              >
                {formattedAmount(transaction?.de006CardholderBillingAmount)}
              </p>

              {transaction?.eligibleRogersRedeemBonus && (
                <p
                  aria-label={
                    rb_rewards_cashback.rewardsCashback
                      .eligibleforRogersRedemption
                  }
                  className="text-[10.5px] text-[#1f1f1f] border border-[#003D46] bg-[#F1FDFF] px-3 mt-2 max-w-fit self-start"
                >
                  {
                    rb_rewards_cashback.rewardsCashback
                      .eligibleforRogersRedemption
                  }
                </p>
              )}
            </>
          )}
        </div>

        {!isEligibleForRedemption && (
          <div className="flex flex-col flex-1 justify-end items-end">
            <p
              aria-label={transaction?.de006CardholderBillingAmount}
              className="text-[15px] text-black font-semibold"
            >
              {formattedAmount(transaction?.de006CardholderBillingAmount)}
            </p>
            <p
              aria-label={transaction?.cashBackAmount}
              className="text-[12px] text-[#1E8240]"
            >
              {formattedAmount(transaction?.cashBackAmount)}
            </p>
            <p
              aria-label={rb_rewards_cashback?.rewardsCashback.cashBackRedeemed}
              className="text-[12px] text-[#1E8240]"
            >
              {rb_rewards_cashback?.rewardsCashback.cashBackRedeemed}
            </p>
          </div>
        )}

        {isEligibleForRedemption && (
          <button
            aria-label={rb_rewards_cashback.rewardsCashback.redeem}
            className={`${disableRedeemButton ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border' : 'bg-white text-[#DA291C] hover:bg-red-100 border border-[#DA291C]'} h-[48px] w-[100px] font-medium py-2 px-4 focus:outline-none focus:ring-2 focus:ring-opacity-50"`}
            onClick={() => handleRedeemClick(transaction)}
            disabled={disableRedeemButton}
          >
            {rb_rewards_cashback.rewardsCashback.redeem}
          </button>
        )}
      </div>
      <div className="h-[1px] bg-[#E9E9E9]" />
    </div>
  );
};

export default RewardTransactionItem;
